/**
 * Shared Tailwind class-fragment tokens for form controls and overlay surfaces.
 *
 * These tokens eliminate duplication across Input, Textarea, SelectTrigger,
 * and any future input-like primitives, as well as overlay surfaces like
 * SelectContent, PopoverContent, and DialogContent.  They are plain strings
 * so they compose with `cn()` without any runtime overhead.
 */

/** Focus-visible ring used by most input-like controls. */
export const FOCUS_RING_BASE =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-0 focus-visible:border-primary/50";

/** Disabled state shared by all form controls. */
export const DISABLED_BASE =
  "disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Common visual base for input-like controls (Input, Textarea, SelectTrigger).
 *
 * Includes: rounded border, background, subtle shadow, transition, focus ring,
 * and disabled state.  Component-specific classes (height, padding, text-size,
 * placeholder, file-input resets, etc.) are still applied at the call-site.
 */
export const FORM_CONTROL_BASE = [
  "rounded-lg border border-input bg-background",
  "shadow-sm shadow-black/[0.03] dark:shadow-black/10",
  "ring-offset-background transition-[border-color,box-shadow] duration-150",
  FOCUS_RING_BASE,
  DISABLED_BASE,
].join(" ");

/* -------------------------------------------------------------------------- */
/*  Peer-driven indicator tokens (Checkbox, Radio, Switch)                    */
/*                                                                            */
/*  These controls use a hidden `peer sr-only` <input> and style a visible    */
/*  sibling via `peer-*:` variants — keeping visual state fully in CSS.       */
/* -------------------------------------------------------------------------- */

/** Focus ring for a visible indicator whose hidden input sibling is a `peer`. */
export const PEER_FOCUS_RING =
  "peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background";

/** Disabled styling for a visible indicator driven by a `peer` input. */
export const PEER_DISABLED =
  "peer-disabled:cursor-not-allowed peer-disabled:opacity-50";

/* -------------------------------------------------------------------------- */
/*  Overlay surface tokens (SelectContent, PopoverContent, DialogContent …)   */
/* -------------------------------------------------------------------------- */

/** Elevated shadow shared by all overlay surfaces. */
export const OVERLAY_SHADOW =
  "shadow-lg shadow-black/[0.08] dark:shadow-black/30";

/**
 * Visual base for popper-positioned overlay surfaces (SelectContent,
 * PopoverContent).  Includes z-index, rounding, border, popover colours,
 * and the elevated shadow.
 *
 * Dialog uses a different background / rounding so it applies OVERLAY_SHADOW
 * directly instead of this composite token.
 */
export const OVERLAY_SURFACE_BASE = [
  "z-50 rounded-lg border bg-popover text-popover-foreground",
  OVERLAY_SHADOW,
].join(" ");

/**
 * Shared open/close animations for popper-positioned overlays.
 *
 * Includes fade, zoom-95, and directional slide based on `data-[side]`.
 * Dialog has its own responsive animation rules so it does not use this token.
 */
export const OVERLAY_ANIMATION_BASE = [
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
  "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
  "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
].join(" ");
